import React from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';

const AnalyticsScreen = () => {
  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.iconContainer}>
          <Ionicons name="bar-chart" size={48} color={theme.colors.primary} />
        </View>
        <Text style={styles.title}>Analytics Dashboard</Text>
        <Text style={styles.subtitle}>Advanced Business Intelligence</Text>
      </View>

      {/* Coming Soon Badge */}
      <View style={styles.comingSoonBadge}>
        <Ionicons name="time" size={20} color={theme.colors.orange600} />
        <Text style={styles.comingSoonText}>Coming Soon</Text>
      </View>

      {/* Feature Preview Cards */}
      <View style={styles.featuresGrid}>
        
        {/* Revenue Analytics */}
        <View style={styles.featureCard}>
          <View style={styles.featureIconContainer}>
            <Ionicons name="trending-up" size={32} color={theme.colors.green600} />
          </View>
          <Text style={styles.featureTitle}>Revenue Analytics</Text>
          <Text style={styles.featureDescription}>
            Detailed revenue tracking, profit margins, and financial forecasting with interactive charts
          </Text>
        </View>

        {/* Fleet Performance */}
        <View style={styles.featureCard}>
          <View style={styles.featureIconContainer}>
            <Ionicons name="speedometer" size={32} color={theme.colors.blue600} />
          </View>
          <Text style={styles.featureTitle}>Fleet Performance</Text>
          <Text style={styles.featureDescription}>
            Vehicle utilization rates, fuel efficiency metrics, and maintenance cost analysis
          </Text>
        </View>

        {/* Driver Insights */}
        <View style={styles.featureCard}>
          <View style={styles.featureIconContainer}>
            <Ionicons name="people" size={32} color={theme.colors.purple600} />
          </View>
          <Text style={styles.featureTitle}>Driver Insights</Text>
          <Text style={styles.featureDescription}>
            Performance ratings, earnings analysis, and productivity benchmarks for all drivers
          </Text>
        </View>

        {/* Predictive Analytics */}
        <View style={styles.featureCard}>
          <View style={styles.featureIconContainer}>
            <Ionicons name="analytics" size={32} color={theme.colors.orange600} />
          </View>
          <Text style={styles.featureTitle}>Predictive Analytics</Text>
          <Text style={styles.featureDescription}>
            AI-powered insights for demand forecasting, maintenance scheduling, and business growth
          </Text>
        </View>

        {/* Custom Reports */}
        <View style={styles.featureCard}>
          <View style={styles.featureIconContainer}>
            <Ionicons name="document-text" size={32} color={theme.colors.red600} />
          </View>
          <Text style={styles.featureTitle}>Custom Reports</Text>
          <Text style={styles.featureDescription}>
            Build custom dashboards and automated reports tailored to your business needs
          </Text>
        </View>

        {/* Real-time Monitoring */}
        <View style={styles.featureCard}>
          <View style={styles.featureIconContainer}>
            <Ionicons name="pulse" size={32} color={theme.colors.indigo600} />
          </View>
          <Text style={styles.featureTitle}>Real-time Monitoring</Text>
          <Text style={styles.featureDescription}>
            Live fleet tracking, instant alerts, and real-time performance monitoring
          </Text>
        </View>

      </View>

      {/* Development Timeline */}
      <View style={styles.timelineContainer}>
        <Text style={styles.timelineTitle}>Development Roadmap</Text>
        
        <View style={styles.timelineItem}>
          <View style={[styles.timelineDot, { backgroundColor: theme.colors.green600 }]} />
          <View style={styles.timelineContent}>
            <Text style={styles.timelinePhase}>Phase 1 - Foundation</Text>
            <Text style={styles.timelineDescription}>Basic analytics infrastructure and data collection</Text>
            <Text style={styles.timelineStatus}>✅ Completed</Text>
          </View>
        </View>

        <View style={styles.timelineItem}>
          <View style={[styles.timelineDot, { backgroundColor: theme.colors.blue600 }]} />
          <View style={styles.timelineContent}>
            <Text style={styles.timelinePhase}>Phase 2 - Core Features</Text>
            <Text style={styles.timelineDescription}>Revenue tracking, fleet performance, and basic reporting</Text>
            <Text style={styles.timelineStatus}>🚧 In Development</Text>
          </View>
        </View>

        <View style={styles.timelineItem}>
          <View style={[styles.timelineDot, { backgroundColor: theme.colors.orange600 }]} />
          <View style={styles.timelineContent}>
            <Text style={styles.timelinePhase}>Phase 3 - Advanced Analytics</Text>
            <Text style={styles.timelineDescription}>AI insights, predictive analytics, and custom dashboards</Text>
            <Text style={styles.timelineStatus}>📋 Planned</Text>
          </View>
        </View>

        <View style={styles.timelineItem}>
          <View style={[styles.timelineDot, { backgroundColor: theme.colors.purple600 }]} />
          <View style={styles.timelineContent}>
            <Text style={styles.timelinePhase}>Phase 4 - Integration</Text>
            <Text style={styles.timelineDescription}>Third-party integrations and mobile optimization</Text>
            <Text style={styles.timelineStatus}>🔮 Future</Text>
          </View>
        </View>
      </View>

      {/* Contact Info */}
      <View style={styles.contactContainer}>
        <Text style={styles.contactTitle}>Stay Updated</Text>
        <Text style={styles.contactDescription}>
          The Analytics Dashboard is currently under development. Advanced reporting features will be available soon.
        </Text>
      </View>

    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  contentContainer: {
    padding: theme.spacing.lg,
  },
  header: {
    alignItems: 'center',
    marginBottom: theme.spacing.xl,
  },
  iconContainer: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: `${theme.colors.primary}10`,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing.md,
  },
  title: {
    fontSize: theme.typography.sizes.xxxl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: theme.typography.sizes.lg,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  comingSoonBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `${theme.colors.orange600}15`,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.full,
    marginBottom: theme.spacing.xl,
    gap: theme.spacing.sm,
  },
  comingSoonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.orange600,
  },
  featuresGrid: {
    gap: theme.spacing.lg,
    marginBottom: theme.spacing.xl,
  },
  featureCard: {
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    ...theme.shadows.md,
  },
  featureIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.gray50,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing.md,
  },
  featureTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.sm,
  },
  featureDescription: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
    lineHeight: 22,
  },
  timelineContainer: {
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    ...theme.shadows.md,
    marginBottom: theme.spacing.xl,
  },
  timelineTitle: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.lg,
    textAlign: 'center',
  },
  timelineItem: {
    flexDirection: 'row',
    marginBottom: theme.spacing.lg,
  },
  timelineDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginTop: 6,
    marginRight: theme.spacing.md,
  },
  timelineContent: {
    flex: 1,
  },
  timelinePhase: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  timelineDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs,
  },
  timelineStatus: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textTertiary,
    fontStyle: 'italic',
  },
  contactContainer: {
    backgroundColor: `${theme.colors.primary}08`,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
  },
  contactTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.primary,
    marginBottom: theme.spacing.sm,
  },
  contactDescription: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
  },
});

export default AnalyticsScreen;
